import { openCropDialog } from './crop-control.js';
import { productBackdropRect } from './product-backdrop.js';
import { openInlineCrop, type InlineCropHandle } from './inline-crop.js';
import type { DesignerProductBackdrop } from '../designer-types.js';
import {
  applyDesignerCommand,
  boxExtent,
  boxOverhangs,
  expandedLayerId,
  formatZoneSize,
  layerScene,
  zoneScene,
  qualityFor,
  effectiveDpi,
  shapeDef,
  shapePath,
  stickerContourFromRaster,
  STICKER_LIMITS,
  stickerPreviewPxPerMm,
  stickerVerdict,
  type Color,
  type DesignBox,
  type DesignLayer,
  type DesignTextLayer,
  type DesignAreaAnswer,
  designerVerticalText,
  designerPatternTiles,
} from '@pz/scene';
import {
  boxDiffers,
  boxFromInk,
  boxFromInkKeepingWidth,
  autoTextLaysOutAtBoxWidth,
  clamp,
  hitTest,
  inkBounds,
  layerContentHash,
  layerFitKey,
  layerPadMm,
  round,
  snapAngle,
  snapBox,
  textAdvanceMm,
  textBoxSize,
  textLines,
  type SnapGuide,
} from './state.js';
import { el, eventInside, hexOf, iconSvg, type DesignerCtx, type IconName } from './panels.js';
import { observeSize } from '../layers.js';
import { canDelete, canEdit, run } from './commands.js';
import { SCALE_FACTORS, arrowDelta, matchShortcut, nudgeStepMm } from './keyboard.js';
import { clampZoom, nextZoom } from './zoom.js';
import { openContextMenu } from './context-menu.js';
import { openPopover } from './overlay.js';
import { createColorBlock, openFontPicker } from './controls.js';
import { startReplace, toggleRemoveBackground } from './image-panel.js';
import {
  anchorBoxHeight,
  anchorVOf,
  canHaveFixedWidth,
  fixedBoxHeightMm,
  fixedByDragPatch,
  isFixedTextBox,
  linesFromHeightMm,
  minWrapWidthMm,
  sideWidthBox,
  wrapLineCount,
} from './text-box.js';
import { PROBLEM_TEXTS, problemMessage } from './texts.js';

/**
 * The working canvas: the print zone at the largest size that fits, with the
 * shopper's layers on it as draggable DOM nodes.
 *
 * Why DOM nodes and not one big canvas: the widget's gesture rule
 * (canvas-edit.ts) is CSS during the gesture, one render on release. A drag
 * here costs a `transform` on one element — GPU-composited, 60 fps on a cheap
 * phone — while the Skia raster underneath is untouched, because nothing about
 * a layer's PIXELS depends on where it sits. Only a size or content change
 * re-rasterises, once, when the fingers lift. The alternative (re-render the
 * whole zone per pointermove) is exactly the jank the photo editor was rebuilt
 * to remove.
 *
 * Every layer's picture is the real renderer's output for `layerScene` — the
 * same Skia that makes the print file — so what the shopper drags is what will
 * be printed, including the outline, the shadow and the bend. Until that
 * bundle loads (several megabytes of WASM) the canvas shows DOM stand-ins and
 * says so; `renderer.onReady` swaps them for the real thing.
 */

/** Padding between the zone and the stage edge. */
const PAD_DESKTOP = 26;
const PAD_PHONE = 14;
/** Ruler thickness; dropped when the stage is too small to spare it. */
const RULER = 18;
/**
 * Designing on the product: at 100 % the zone takes this many times its share
 * of the product picture, so a chest print shows the shirt around it — and
 * never less than PRODUCT_FIT_MIN of the stage, or a tiny pocket print would
 * be unworkable. Zoom still goes all the way in.
 */
const PRODUCT_FIT_SPAN = 1.6;
const PRODUCT_FIT_MIN = 0.5;
/** A phone has a third of the width to work in; the zone keeps more of it. */
const PRODUCT_FIT_MIN_PHONE = 0.72;
/** Device pixels per CSS pixel we are willing to rasterise at. */
const DPR_CAP = 2;
/** Font size the 2D fallback measures at, for a stable em ratio. */
const MEASURE_PX = 100;

type HandleId = 'nw' | 'ne' | 'se' | 'sw' | 'n' | 'e' | 's' | 'w' | 'rot';

/** The four that change the aspect (images and shapes only). */
const SIDE_HANDLES: ReadonlySet<string> = new Set(['n', 's', 'e', 'w']);

interface LayerView {
  id: string;
  node: HTMLElement;
  raster?: HTMLCanvasElement;
  draft?: HTMLElement;
  badge?: HTMLElement;
  tiles?: Map<string, { node: HTMLElement; image: HTMLImageElement }>;
  tileUrl?: string;
  /** Content hash of the picture currently shown. */
  hash?: string;
  /** Scale (px/mm) and dpr the picture was made at. */
  scaleKey?: string;
  padMm: number;
  /** How many times the box has been re-fitted for this ink. */
  fitKey?: string;
  fits: number;
  /** How many times a clipped raster has been grown for this ink. */
  grows: number;
  /** The box the last fit produced, so a settled fit stops asking. */
  fittedTo?: DesignBox;
  /** The measurement that produced `textWidthMm`. */
  measureKey?: string;
}

interface Drag {
  kind: 'move' | 'corner' | 'side' | 'rotate' | 'pinch';
  id: string;
  handle?: HandleId;
  startBox: DesignBox;
  startSizeMm?: number;
  live: DesignBox;
  liveSizeMm?: number;
  /** Zone rect in client coordinates, frozen for the gesture. */
  rect: { left: number; top: number };
  pointers: Map<number, { x: number; y: number }>;
  startPointer: { x: number; y: number };
  startCentre: { x: number; y: number };
  startAngle: number;
  startDist: number;
  startMid: { x: number; y: number };
  shift: boolean;
  moved: boolean;
  others: DesignLayer[];
}

export interface CanvasOptions {
  /** A double tap or long press on a layer: open its own tool panel. */
  onOpenLayer(layer: DesignLayer): void;
  /** The T shortcut, and the empty zone's "Tekst" button. */
  onAddText?(text?: string): void;
  /**
   * The contour the canvas traced for an answer, handed back so the validator
   * can turn it into `sticker-edge` / `sticker-islands`.
   */
  onStickerContour?(answer: DesignAreaAnswer, verdict: { touchesEdge: boolean; islands: number }): void;
}

/**
 * The platform's live contour helper, when it is there.
 *
 * `packages/scene` grows `stickerContourPreview` in the same programme as this
 * designer: a low-resolution contour the editor can afford to recompute while
 * a layer is being dragged. Reading it off the namespace keeps the widget
 * compiling against a scene package that does not export it yet, and picks it
 * up the moment it does.
 */
/** What the contour trace reports back, and what the validator asks for. */
type ContourVerdict = { touchesEdge: boolean; islands: number };

export interface DesignerCanvas {
  resetPan():void;
  /**
   * Zazzle's inline crop on this photo layer. False when the layer cannot be
   * cropped in place (turned, straightened, contained) — the caller then opens
   * the full crop dialog.
   */
  startInlineCrop(id: string): boolean;
  /** Replaces the product picture behind the zone (another colour, another side). */
  setProductBackdrop(backdrop: DesignerProductBackdrop | undefined): void;
  /** The product's average colour behind the zone, when a picture is shown. */
  productColor(): [number, number, number] | undefined;
  readonly element: HTMLElement;
  /** Re-measures the stage and re-fits the zone. */
  relayout(): void;
  /** Re-reads the answer: new nodes, gone nodes, changed pictures. */
  sync(): void;
  /** Forces a repaint of one layer (or all of them). */
  invalidate(layerId?: string): void;
  destroy(): void;
}

export function createCanvas(ctx: DesignerCtx, options: CanvasOptions): DesignerCanvas {
  const cfg = ctx.config;
  const main = el('div', 'pzd-main');
  const stage = el('div', 'pzd-stage pzd-canvas');
  stage.setAttribute('role', 'application');
  stage.setAttribute('aria-label', ctx.texts.canvasLabel);
  main.appendChild(stage);

  const rulerH = el('div', 'pzd-ruler pzd-ruler-h');
  const rulerV = el('div', 'pzd-ruler pzd-ruler-v');
  const zone = el('div', 'pzd-zone');
  const safe = el('div', 'pzd-safe');
  const guides = el('div', 'pzd-guides');
  const grid = el('div', 'pzd-canvas-grid'); grid.hidden=true;
  const composition = el('canvas','pzd-composition'); composition.hidden=true;
  const multi = el('div','pzd-multi');
  const selection = el('div', 'pzd-sel');
  selection.hidden = true;
  const actions = el('div', 'pzd-actions');
  actions.hidden = true;
  const sizeTag = el('div', 'pzd-size-tag');
  const empty = el('div', 'pzd-empty');
  const backdropPick = el('div', 'pzd-backdrop-pick');
  const backing = el('canvas', 'pzd-sticker-backing'); backing.hidden = true;
  const stickerStatus = el('div', 'pzd-sticker-status'); stickerStatus.hidden = true; stickerStatus.setAttribute('role', 'status');
  // The cut line, the visible edge and the safe area, in the reference's own
  // colours. Bleed only exists when the product has one (`config.bleedMm`).
  const bleed = el('div', 'pzd-bleed'); bleed.hidden = true;
  // Dimension callouts: a thin capped line down the left and along the bottom
  // with the zone's real size on it, which is how a shopper checks that "7,5
  // cm" means what they think it means.
  const dimH = el('div', 'pzd-dim is-h');
  const dimV = el('div', 'pzd-dim is-v');
  // Drag on empty canvas gathers everything the rectangle touches.
  const marquee = el('div', 'pzd-marquee'); marquee.hidden = true;
  // The product the zone is printed on (Zazzle's shirt behind the artboard).
  // Below everything, never hit-tested, never part of any render.
  const productPic = el('img', 'pzd-product-pic');
  productPic.alt = '';
  productPic.draggable = false;
  productPic.hidden = true;
  productPic.setAttribute('aria-hidden', 'true');
  productPic.addEventListener('load', () => { sampleProduct(); paintProduct(); });
  productPic.addEventListener('error', () => {
    productFailed = true;
    if (backdrop.kind === 'product') backdrop = { kind: 'light' };
    paintBackdrop();
  });
  let productFailed = false;
  zone.append(backing);
  zone.append(safe, guides, selection);
  stage.append(productPic, bleed, rulerH, rulerV, zone, dimH, dimV, sizeTag, empty, actions, backdropPick, marquee, stickerStatus);

  /** CSS px per millimetre; the one number the whole canvas is built from. */
  let scale = 1;
  let zoneLeft = 0;
  let zoneTop = 0;
  /** The host's product picture; replaced live when the shopper picks another colour. */
  let product: DesignerProductBackdrop | undefined = ctx.productBackdrop;
  let backdrop: { kind: 'light' | 'dark' | 'custom' | 'product'; hex?: string } =
    product ? { kind: 'product' } : { kind: 'light' };
  /** Whether the last layout fitted the zone for the product view. */
  let laidOutOnProduct = false;
  /** The inline crop in progress, if any. */
  let inlineCrop: InlineCropHandle | undefined;
  if (product) productPic.src = product.url;

  /**
   * The product's colour right behind the print zone, averaged from the
   * picture, so new words can pick ink that shows up on it (`contrast.ts`).
   * Undefined until the picture has loaded, or when it cannot be read.
   */
  let productRgb: [number, number, number] | undefined;
  function sampleProduct(): void {
    productRgb = undefined;
    if (!product || !productPic.naturalWidth) return;
    try {
      // The whole picture first, then the zone's pixels: a source rectangle
      // taken straight from an SVG draws nothing in some engines.
      const W = 96, H = Math.max(1, Math.round(96 * productPic.naturalHeight / productPic.naturalWidth));
      const probe = document.createElement('canvas');
      probe.width = W; probe.height = H;
      const g = probe.getContext('2d', { willReadFrequently: true });
      if (!g) return;
      g.drawImage(productPic, 0, 0, W, H);
      const { x, y, w, h } = product.zone;
      const data = g.getImageData(Math.floor(x * W), Math.floor(y * H), Math.max(1, Math.ceil(w * W)), Math.max(1, Math.ceil(h * H))).data;
      let r = 0, gr = 0, b = 0, n = 0;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3]! < 128) continue;
        r += data[i]!; gr += data[i + 1]!; b += data[i + 2]!; n++;
      }
      if (n > 24) productRgb = [r / n, gr / n, b / n];
    } catch {
      // A cross-origin picture cannot be read; the default ink stays.
    }
  }

  function setProductBackdrop(next: DesignerProductBackdrop | undefined): void {
    const had = Boolean(product);
    product = next;
    productFailed = false;
    if (!next) productRgb = undefined;
    if (next) {
      // A shopper who never chose a plain backdrop keeps seeing the product.
      if (!had || backdrop.kind === 'product') backdrop = { kind: 'product' };
      if (productPic.src !== next.url) productPic.src = next.url;
    } else {
      productPic.removeAttribute('src');
      if (backdrop.kind === 'product') backdrop = { kind: 'light' };
    }
    paintBackdrop();
  }

  const views = new Map<string, LayerView>();
  const guideNodes: HTMLElement[] = [];
  let drag: Drag | undefined;
  let longPress: number | undefined;
  let lastTap = { id: '', at: 0 };
  let outsideWarnedAt = 0;
  let stopped = false;
  let backingKey = '', backingRequest = 0, backingTimer: number | undefined;
  let compositionKey='',compositionRequest=0,compositionTimer:number|undefined;
  let panX=0,panY=0,lastZoom=1,space=false;
  let panning:{pointer:number;x:number;y:number;panX:number;panY:number}|undefined;
  /** The rubber band, while a drag on empty canvas is in progress. */
  let band: { pointer: number; startX: number; startY: number } | undefined;
  /** The group whose members are individually selectable (double-click). */
  let editingGroup: string | undefined;
  function syncComposition(value:DesignAreaAnswer=ctx.store.value()):void {
    const wanted=ctx.view.review;
    if(!wanted){compositionKey='';compositionRequest++;window.clearTimeout(compositionTimer);composition.hidden=true;zone.classList.remove('has-composition');return;}
    const answer={...value,layers:value.layers.filter(l=>ctx.view.review||!ctx.isHidden(l.id))};
    const key=JSON.stringify([answer,Math.round(scale*100),ctx.view.review]);if(key===compositionKey)return;compositionKey=key;
    // Keep the last complete frame until its replacement is ready.
    const request=++compositionRequest;window.clearTimeout(compositionTimer);
    compositionTimer=window.setTimeout(()=>{
      const density=Math.min(scale*dpr(),1600/Math.max(cfg.sizeMm.w,cfg.sizeMm.h));
      const scene=zoneScene(ctx.rawConfig,answer,density);
      void ctx.renderer.renderRaw?.(scene,1).then(out=>{
        if(stopped||request!==compositionRequest)return;
        if(!out)throw new Error('Sačekajte da se učita prikaz za štampu.');
        composition.width=out.width;composition.height=out.height;
        composition.getContext('2d')?.putImageData(new ImageData(new Uint8ClampedArray(out.pixels),out.width,out.height),0,0);
        composition.hidden=false;zone.classList.add('has-composition');stage.classList.remove('is-draft');
      }).catch(()=>{if(request===compositionRequest){composition.hidden=true;zone.classList.remove('has-composition');stage.classList.add('is-draft');compositionKey='';}});
    },80);
  }
  /**
   * The white backing and the dashed cut guide under the design.
   *
   * Two things this must not do. It must not recompute when the shopper only
   * ZOOMED — the backing is a picture of the design, not of the viewport, so
   * the key is the answer and the hidden set, and the canvas element is simply
   * stretched (the raster is made at a density generous enough for the zoom
   * range). And it must not include layers the shopper hid while working: a
   * contour welded around a hidden photograph is a cut file for a sticker
   * nobody ordered, so the hidden ids go through to `zoneScene`.
   */
  function hiddenLayerIds(answer: DesignAreaAnswer): string[] {
    return answer.layers.filter((layer) => ctx.isHidden(layer.id)).map((layer) => layer.id);
  }

  function stickerScene(answer: DesignAreaAnswer, pxPerMm: number): ReturnType<typeof zoneScene> {
    const built = zoneScene(ctx.rawConfig, answer, pxPerMm, { hiddenLayerIds: hiddenLayerIds(answer) });
    built.elements = built.elements.map((element) => (
      element.type === 'sticker' ? { ...element, backingOnly: true, showCutGuide: true } : element
    ));
    return built;
  }

  /**
   * The scene the CONTOUR is traced from: the design with the sticker taken
   * off, so the alpha is the artwork itself rather than the white backing that
   * the artwork produced. Same shape as `stickerSource` in scene-render, which
   * is what the production trace uses — the two must agree or the shopper is
   * shown a shape the press will not cut.
   */
  function contourScene(answer: DesignAreaAnswer, pxPerMm: number): ReturnType<typeof zoneScene> {
    return zoneScene(
      { ...ctx.rawConfig, sticker: undefined },
      { ...answer, sticker: undefined },
      pxPerMm,
      { hiddenLayerIds: hiddenLayerIds(answer) },
    );
  }

  /**
   * Trace the contour of an answer and hand the verdict back.
   *
   * The platform's editor seam is deliberately split: `@pz/scene-render`'s
   * `stickerContourPreview` needs a `ResourceSource` (Node), while the widget
   * owns a renderer that hands back pixels — so the pure trio in `@pz/scene`
   * does the millimetre arithmetic and the tracing over a raster we produce
   * ourselves. One trace in flight at a time: Skia is single-threaded on the
   * main thread and a second request would only queue behind the first while
   * the drag went on.
   */
  let tracing = false;
  let tracedKey = '';
  /** The last verdict traced, for the status pill while a picture redraws. */
  let lastVerdict: ContourVerdict | undefined;
  /**
   * One trace in flight, and at most one waiting.
   *
   * Dropping a request that arrives mid-trace is not good enough: a text layer
   * is re-fitted to its ink a few times right after it renders, so the LAST
   * answer — the one the validator will ask about — is exactly the one whose
   * request would be dropped, and the verdict would then never match.
   */
  let pendingTrace: DesignAreaAnswer | undefined;

  async function traceContour(answer: DesignAreaAnswer, live: boolean): Promise<void> {
    const settings = answer.sticker;
    const raw = ctx.renderer.renderRaw;
    if (!settings || !raw) return;
    if (tracing) {
      pendingTrace = answer;
      return;
    }
    const key = JSON.stringify([answer, hiddenLayerIds(answer)]);
    if (!live && key === tracedKey) return;
    tracing = true;
    try {
      const pxPerMm = stickerPreviewPxPerMm(cfg.sizeMm);
      const out = await raw.call(ctx.renderer, contourScene(answer, pxPerMm), 1);
      if (stopped || !out || out.width <= 0) return;
      const border = settings.imageBorder === false && answer.layers.some(layer => layer.kind === 'image')
        ? await raw.call(ctx.renderer, contourScene({ ...answer, layers: answer.layers.filter(layer => layer.kind === 'image') }, pxPerMm), 1)
        : undefined;
      if (stopped) return;
      const verdict = stickerVerdict(
        stickerContourFromRaster(out.pixels, out.width, out.height, settings, pxPerMm, border?.pixels),
      );
      tracedKey = key;
      lastVerdict = verdict;
      // From here on it is an ordinary blocking problem: the header chip, the
      // Pregled list and the Nalepnica panel all read it off `ctx.problems()`.
      options.onStickerContour?.(answer, verdict);
      paintStickerStatus(verdict);
    } catch {
      // A design too small to trace, or settings the validator will refuse:
      // the full-resolution check at checkout is the authority either way.
    } finally {
      tracing = false;
      const queued = pendingTrace;
      pendingTrace = undefined;
      if (queued && !stopped) void traceContour(queued, false);
    }
  }

  function paintStickerStatus(verdict?: ContourVerdict): void {
    if (!ctx.store.value().sticker) {
      stickerStatus.hidden = true;
      return;
    }
    stickerStatus.hidden = false;
    stickerStatus.classList.toggle('is-bad', Boolean(verdict && (verdict.touchesEdge || verdict.islands > STICKER_LIMITS.maxBridgedIslands)));
    if (verdict?.touchesEdge) {
      stickerStatus.textContent = PROBLEM_TEXTS['sticker-edge']!;
      return;
    }
    if (verdict && verdict.islands > STICKER_LIMITS.maxBridgedIslands) {
      stickerStatus.textContent = PROBLEM_TEXTS['sticker-islands']!;
      return;
    }
    // Never `problem.detail`: that is the platform's English diagnostic. The
    // shopper reads the Serbian sentence from texts.ts, the same one the
    // Pregled list and the Nalepnica panel show.
    const reported = ctx.problems().find((problem) => problem.code.startsWith('sticker-'));
    stickerStatus.textContent = reported ? problemMessage(reported) : ctx.texts.stickerReady;
  }

  function syncBacking(): void {
    const answer = ctx.store.value();
    const key = JSON.stringify([answer, hiddenLayerIds(answer)]);
    if (key === backingKey) return;
    backingKey = key;
    const request = ++backingRequest;
    window.clearTimeout(backingTimer);
    if (!answer.sticker || !answer.layers.length) {
      backing.hidden = true;
      stickerStatus.hidden = true;
      return;
    }
    stickerStatus.hidden = false;
    stickerStatus.textContent = drag ? ctx.texts.stickerDuringDrag : ctx.texts.stickerPreparing;
    // The VERDICT is traced independently of the backing picture, and first.
    // `prepareStickerImage` refuses a contour whose border is clipped — it
    // feeds production, so refusing is right — and that refusal is precisely
    // the state the shopper most needs explained. Tracing separately means the
    // explanation survives the picture failing to draw.
    void traceContour(answer, false);
    backingTimer = window.setTimeout(() => {
      const density = Math.min(
        Math.max(scale, 4) * (window.devicePixelRatio || 1),
        1000 / cfg.sizeMm.w,
      );
      void ctx.renderer.renderRaw?.(stickerScene(answer, density), 1).then((out) => {
        if (stopped || request !== backingRequest) return;
        if (!out) {
          backingKey = '';
          stickerStatus.textContent = 'Sačekajte prikaz za štampu.';
          return;
        }
        backing.width = out.width;
        backing.height = out.height;
        backing.getContext('2d')?.putImageData(new ImageData(new Uint8ClampedArray(out.pixels), out.width, out.height), 0, 0);
        backing.hidden = false;
        paintStickerStatus(lastVerdict);
      }).catch(() => {
        if (stopped || request !== backingRequest) return;
        // No picture, but the trace above has (or is about to have) the reason,
        // in Serbian. The renderer's own sentence is an English production
        // message and has no business on a shopper's screen.
        backing.hidden = true;
        paintStickerStatus(lastVerdict);
      });
    }, drag ? 60 : 160);
  }

  // ── Rulers, safe area, layout ─────────────────────────────────────────────

  function pad(): number {
    return ctx.phone() ? PAD_PHONE : PAD_DESKTOP;
  }

  let layoutPending = false;
  function relayout(): void {
    if (stopped) return;
    if (drag) { layoutPending = true; return; }
    layoutPending = false;
    const width = stage.clientWidth || main.clientWidth || 320;
    const height = stage.clientHeight || 240;
    const rulers = ctx.view.rulers && !ctx.view.review && width > 300 && height > 240;
    rulerH.hidden = !rulers;
    rulerV.hidden = !rulers;
    const gutter = rulers ? RULER : 0;
    const p = pad();
    // No reservation for the sticker status: it is a pill floating inside the
    // stage now, so turning the sticker on no longer shrinks the artboard.
    const availW = Math.max(40, width - gutter - p * 2);
    const availH = Math.max(40, height - gutter - p * 2);
    if(ctx.view.zoom===1&&lastZoom!==1){panX=0;panY=0;}lastZoom=ctx.view.zoom;
    // On the product, 100 % shows the product around the zone the way the
    // reference does — a zone filling the stage would show nothing but fabric.
    // The fit is capped by the picture itself, so it never zooms out past it.
    laidOutOnProduct = productShown();
    const productFit = laidOutOnProduct && product
      ? Math.max(ctx.phone() ? PRODUCT_FIT_MIN_PHONE : PRODUCT_FIT_MIN, Math.min(1, Math.max(product.zone.w, product.zone.h) * PRODUCT_FIT_SPAN))
      : 1;
    scale = Math.max(0.05, Math.min(availW / cfg.sizeMm.w, availH / cfg.sizeMm.h))*ctx.view.zoom*productFit;
    const zoneW = cfg.sizeMm.w * scale;
    const zoneH = cfg.sizeMm.h * scale;
    panX=clamp(panX,-Math.max(zoneW,width)/2,Math.max(zoneW,width)/2);panY=clamp(panY,-Math.max(zoneH,height)/2,Math.max(zoneH,height)/2);
    zoneLeft = Math.round(gutter + p + (availW - zoneW) / 2+panX);
    zoneTop = Math.round(gutter + p + (availH - zoneH) / 2+panY);
    stage.classList.toggle('is-panning',ctx.view.panMode);
    grid.hidden=!ctx.view.grid||ctx.view.review;grid.style.backgroundSize=`${5*scale}px ${5*scale}px`;
    zone.style.left = `${zoneLeft}px`;
    zone.style.top = `${zoneTop}px`;
    zone.style.width = `${Math.round(zoneW)}px`;
    zone.style.height = `${Math.round(zoneH)}px`;

    const m = cfg.safeMarginMm;
    safe.hidden = m <= 0 || !ctx.view.guidelines || ctx.view.review;
    if (m > 0) {
      safe.style.left = `${m * scale}px`;
      safe.style.top = `${m * scale}px`;
      safe.style.right = `${m * scale}px`;
      safe.style.bottom = `${m * scale}px`;
      safe.title = ctx.texts.safeArea;
    }

    // The placeholder belongs inside the zone, not floating over the stage:
    // it is telling the shopper what THIS rectangle is for.
    // …but a pocket-sized zone on a product photo is too small for three
    // buttons a child can hit, so the quick start may spill past it, centred.
    const emptyW = Math.min(width - 12, Math.max(zoneW, 300));
    const emptyH = Math.min(height - 12, Math.max(zoneH, 230));
    empty.style.left = `${Math.round(clamp(zoneLeft + (zoneW - emptyW) / 2, 6, Math.max(6, width - emptyW - 6)))}px`;
    empty.style.top = `${Math.round(clamp(zoneTop + (zoneH - emptyH) / 2, 6, Math.max(6, height - emptyH - 6)))}px`;
    empty.style.width = `${Math.round(emptyW)}px`;
    empty.style.height = `${Math.round(emptyH)}px`;
    // The card row is sized from this, not from a percentage: the empty
    // overlay's grid track is content-sized, and a percentage of it is circular.
    empty.style.setProperty('--pzd-empty-w', `${Math.round(emptyW)}px`);

    // The bleed line sits OUTSIDE the zone: a background must reach it, which
    // is exactly what the shopper cannot guess without seeing it drawn.
    const bleedMm = Math.max(0, cfg.bleedMm);
    bleed.hidden = bleedMm <= 0 || !ctx.view.guidelines || ctx.view.review;
    if (!bleed.hidden) {
      const grow = bleedMm * scale;
      bleed.style.left = `${Math.round(zoneLeft - grow)}px`;
      bleed.style.top = `${Math.round(zoneTop - grow)}px`;
      bleed.style.width = `${Math.round(zoneW + grow * 2)}px`;
      bleed.style.height = `${Math.round(zoneH + grow * 2)}px`;
      bleed.title = ctx.texts.helpBleed;
    }

    // The pill and the callouts say the same thing; the pill is the fallback
    // for a stage too small to draw the callouts on.
    sizeTag.textContent = formatZoneSize(cfg.sizeMm);
    sizeTag.style.left = `${zoneLeft}px`;
    sizeTag.style.top = `${Math.round(zoneTop + zoneH + 7)}px`;
    paintCallouts(zoneW, zoneH);
    sizeTag.hidden = !dimH.hidden;

    if (rulers) {
      rulerH.style.width = `${width}px`;
      rulerV.style.height = `${height}px`;
      rulerH.innerHTML = rulerSvg('h', width, zoneLeft, cfg.sizeMm.w);
      rulerV.innerHTML = rulerSvg('v', height, zoneTop, cfg.sizeMm.h);
    }

    paintBackdrop();
    sync();
    inlineCrop?.relayout();
  }

  /**
   * The two dimension callouts, as the reference draws them: a capped line
   * down the left of the artboard and another along the bottom, each labelled
   * with the zone's real size. They are hidden with the rulers, and on a stage
   * too small to spare the gutter.
   */
  function paintCallouts(zoneW: number, zoneH: number): void {
    const show = ctx.view.rulers && !ctx.view.review && zoneW > 120 && zoneH > 90 && !ctx.phone();
    dimH.hidden = !show;
    dimV.hidden = !show;
    if (!show) return;
    // "7,5 × 5 cm" split into "7,5 cm" and "5 cm": each callout carries the
    // unit, because one of them is read on its own.
    const whole = formatZoneSize(cfg.sizeMm);
    const [left, right] = whole.split('×');
    const unit = (right ?? '').replace(/[\d.,\s]/g, '');
    dimH.style.left = `${zoneLeft}px`;
    dimH.style.top = `${Math.round(zoneTop + zoneH + 22)}px`;
    dimH.style.width = `${Math.round(zoneW)}px`;
    dimH.textContent = `${(left ?? '').trim()} ${unit}`.trim();
    dimV.style.left = `${Math.round(zoneLeft - 26)}px`;
    dimV.style.top = `${zoneTop}px`;
    dimV.style.height = `${Math.round(zoneH)}px`;
    dimV.textContent = (right ?? '').trim();
  }

  /** Tick spacing in mm: the finest that still leaves the marks apart. */
  function tickStep(): number {
    for (const step of [1, 2, 5, 10, 20, 50]) if (step * scale >= 6) return step;
    return 100;
  }

  function labelStep(step: number): number {
    for (const candidate of [5, 10, 20, 50, 100, 200]) {
      if (candidate >= step && candidate * scale >= 34) return candidate;
    }
    return 500;
  }

  function rulerSvg(axis: 'h' | 'v', length: number, offset: number, sizeMm: number): string {
    const step = tickStep();
    const labels = labelStep(step);
    const parts: string[] = [];
    for (let mm = 0; mm <= sizeMm + 0.001; mm += step) {
      const at = Math.round(offset + mm * scale) + 0.5;
      const major = Math.abs(mm % labels) < 0.001;
      const len = major ? 11 : mm % (step * 5) === 0 ? 8 : 5;
      parts.push(axis === 'h'
        ? `<line x1="${at}" y1="${RULER - len}" x2="${at}" y2="${RULER}"/>`
        : `<line x1="${RULER - len}" y1="${at}" x2="${RULER}" y2="${at}"/>`);
      if (major && mm > 0 && mm < sizeMm) {
        parts.push(axis === 'h'
          ? `<text x="${at + 2}" y="9">${Math.round(mm)}</text>`
          : `<text x="2" y="${at - 2}" transform="rotate(-90 2 ${at - 2})">${Math.round(mm)}</text>`);
      }
    }
    const w = axis === 'h' ? length : RULER;
    const h = axis === 'h' ? RULER : length;
    return (
      `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" aria-hidden="true">` +
      `<g stroke="#a9a9b8" stroke-width="1" fill="#8a8a97" font-size="8" font-family="inherit">${parts.join('')}</g></svg>`
    );
  }

  function productShown(): boolean {
    return backdrop.kind === 'product' && Boolean(product) && !productFailed;
  }

  /** Places the product picture so its print zone is the zone on screen. */
  function paintProduct(): void {
    // Turning the product on or off changes the fit, not just the paint.
    if (productShown() !== laidOutOnProduct) {
      relayout();
      return;
    }
    const on = productShown();
    zone.classList.toggle('is-on-product', on);
    stage.classList.toggle('has-product', on);
    const rect = on && product
      ? productBackdropRect(
        { left: zoneLeft, top: zoneTop, width: cfg.sizeMm.w * scale, height: cfg.sizeMm.h * scale },
        product.zone,
      )
      : undefined;
    productPic.hidden = !rect || !productPic.complete || productPic.naturalWidth === 0;
    if (!rect) return;
    productPic.style.left = `${round(rect.left, 2)}px`;
    productPic.style.top = `${round(rect.top, 2)}px`;
    productPic.style.width = `${round(rect.width, 2)}px`;
    productPic.style.height = `${round(rect.height, 2)}px`;
  }

  function paintZoneBackground(): void {
    zone.classList.toggle('is-dark', backdrop.kind === 'dark');
    paintProduct();
    zone.style.background = backdrop.kind === 'product' && !productFailed
      ? 'transparent'
      : backdrop.kind === 'custom' && backdrop.hex
        ? backdrop.hex
        : backdrop.kind === 'dark' ? '#16161c' : '#ffffff';
    if(ctx.view.transparency)zone.style.background='repeating-conic-gradient(#dddde5 0 25%,#fff 0 50%) 0/14px 14px';
    const printed = ctx.store.value().background;
    if (printed) zone.style.background = printed.hex;
  }

  function paintBackdrop(): void {
    paintZoneBackground();
    backdropPick.textContent = '';
    const swatch = (kind: 'light' | 'dark', color: string, label: string): void => {
      const button = el('button', backdrop.kind === kind ? 'is-on' : '');
      button.type = 'button';
      button.style.background = color;
      button.setAttribute('aria-label', `${ctx.texts.backdrop}: ${label}`);
      button.title = `${ctx.texts.backdrop}: ${label}`;
      button.addEventListener('click', () => {
        backdrop = { kind };
        paintBackdrop();
      });
      backdropPick.appendChild(button);
    };
    if (product && !productFailed) {
      const button = el('button', backdrop.kind === 'product' ? 'is-on is-product' : 'is-product');
      button.type = 'button';
      button.style.backgroundImage = `url("${product.url.replace(/"/g, '%22')}")`;
      const label = `${ctx.texts.backdrop}: ${product.name ?? ctx.texts.backdropProduct}`;
      button.setAttribute('aria-label', label);
      button.title = label;
      button.addEventListener('click', () => {
        backdrop = { kind: 'product' };
        paintBackdrop();
      });
      backdropPick.appendChild(button);
    }
    swatch('light', '#ffffff', ctx.texts.backdropLight);
    swatch('dark', '#16161c', ctx.texts.backdropDark);
    const custom = el('button', backdrop.kind === 'custom' ? 'is-on' : '');
    custom.type = 'button';
    custom.style.background = backdrop.hex ?? 'linear-gradient(135deg,#ffd6e0,#a8d8ff)';
    custom.setAttribute('aria-label', `${ctx.texts.backdrop}: ${ctx.texts.backdropCustom}`);
    custom.title = `${ctx.texts.backdrop}: ${ctx.texts.backdropCustom}`;
    const input = el('input', 'pzd-color-native');
    input.type = 'color';
    input.value = backdrop.hex ?? '#f2c9d4';
    input.setAttribute('aria-label', ctx.texts.backdropCustom);
    input.addEventListener('change', () => {
      backdrop = { kind: 'custom', hex: input.value };
      paintBackdrop();
    });
    custom.appendChild(input);
    custom.addEventListener('click', () => input.click());
    backdropPick.appendChild(custom);
  }

  // ── Geometry helpers ──────────────────────────────────────────────────────

  function applyGeometry(node: HTMLElement, box: DesignBox): void {
    const w = box.w * scale;
    const h = box.h * scale;
    node.style.width = `${w}px`;
    node.style.height = `${h}px`;
    node.style.transform =
      `translate(${round(box.x * scale - w / 2, 2)}px, ${round(box.y * scale - h / 2, 2)}px)` +
      (box.rotation ? ` rotate(${box.rotation}deg)` : '');
  }

  function applyRaster(view: LayerView, box: DesignBox): void {
    const target = view.raster ?? view.draft;
    if (!target) return;
    if (view.raster) {
      const padPx = view.padMm * scale;
      target.style.left = `${round(-padPx, 2)}px`;
      target.style.top = `${round(-padPx, 2)}px`;
      target.style.width = `${round((box.w + view.padMm * 2) * scale, 2)}px`;
      target.style.height = `${round((box.h + view.padMm * 2) * scale, 2)}px`;
    }
  }

  /** Repeat the cached native motif with CSS; no whole-scene render per pointer move. */
  function paintPattern(view: LayerView, layer: DesignLayer): void {
    view.node.classList.toggle('is-pattern', Boolean(layer.pattern));
    if (!layer.pattern) {
      for (const tile of view.tiles?.values() ?? []) tile.node.remove();
      view.tiles?.clear();
      return;
    }
    view.node.style.width = cfg.sizeMm.w * scale + 'px';
    view.node.style.height = cfg.sizeMm.h * scale + 'px';
    view.node.style.transform = 'none';
    if (!view.tileUrl) return;
    let tiles: DesignLayer[];
    try { tiles = designerPatternTiles(layer, cfg.sizeMm); } catch { return; }
    view.tiles ??= new Map();
    const alive = new Set(tiles.map(tile => tile.id));
    for (const [id, tile] of view.tiles) if (!alive.has(id)) { tile.node.remove(); view.tiles.delete(id); }
    for (const tile of tiles) {
      let cached = view.tiles.get(tile.id);
      if (!cached) {
        const node = el('div', 'pzd-pattern-tile'), image = el('img');
        image.alt = ''; image.draggable = false; node.append(image); view.node.append(node);
        cached = { node, image }; view.tiles.set(tile.id, cached);
      }
      applyGeometry(cached.node, tile.box);
      if (cached.image.getAttribute('src') !== view.tileUrl) cached.image.src = view.tileUrl;
      const pad = view.padMm * scale;
      Object.assign(cached.image.style, { left: -pad + 'px', top: -pad + 'px', width: (tile.box.w + 2 * view.padMm) * scale + 'px', height: (tile.box.h + 2 * view.padMm) * scale + 'px',
        transform: 'scale(' + (Boolean(tile.flipX) !== Boolean(layer.flipX) ? -1 : 1) + ',' + (Boolean(tile.flipY) !== Boolean(layer.flipY) ? -1 : 1) + ')' });
    }
  }

  function pointToMm(clientX: number, clientY: number, rect?: { left: number; top: number }): { x: number; y: number } {
    const r = rect ?? zone.getBoundingClientRect();
    return { x: (clientX - r.left) / scale, y: (clientY - r.top) / scale };
  }

  // ── Layer nodes ───────────────────────────────────────────────────────────

  function viewFor(layer: DesignLayer): LayerView {
    const existing = views.get(layer.id);
    if (existing) return existing;
    const node = el('div', 'pzd-layer');
    node.dataset.layer = layer.id;
    const view: LayerView = { id: layer.id, node, padMm: layerPadMm(layer), fits: 0, grows: 0 };
    views.set(layer.id, view);
    zone.appendChild(node);
    return view;
  }

  function sync(): void {
    if (stopped) return;
    // Async font/contour notifications must not reset geometry beneath a pointer.
    if (drag) return;
    syncBacking();
    syncComposition();
    const { answer, selectedId } = ctx.store.state();
    paintZoneBackground();
    const alive = new Set(answer.layers.map((layer) => layer.id));
    for (const [id, view] of [...views]) {
      if (alive.has(id)) continue;
      view.node.remove();
      views.delete(id);
    }
    answer.layers.forEach((layer) => {
      const view = viewFor(layer);
      view.padMm = layerPadMm(layer);
      applyGeometry(view.node, layer.box);
      applyRaster(view, layer.box);
      paintPattern(view, layer);
      view.node.style.opacity = layer.opacity !== undefined && layer.opacity < 1 ? String(layer.opacity) : '';
      view.node.classList.toggle('is-hidden', ctx.isHidden(layer.id) || layer.visible===false);
      const bad = cfg.boundsMode === 'inside' && overhangsZone(layer.box);
      view.node.classList.toggle('is-outside', bad);
      // Paint order is the answer's order; the selection chrome stays on top.
      const index = answer.layers.indexOf(layer);
      const previous = index > 0 ? views.get(answer.layers[index - 1]!.id)?.node : backing;
      if (previous && previous.nextSibling !== view.node) zone.insertBefore(view.node, previous.nextSibling);
      paintBadge(view, layer);
      schedulePaint(layer);
    });
    // Guides and selection chrome always above the layers.
    multi.textContent='';
    if(!ctx.view.review&&ctx.selection.size>1)for(const l of answer.layers.filter(l=>ctx.selection.has(l.id)&&l.visible!==false)){const outline=el('div','pzd-multi-box');applyGeometry(outline,l.box);multi.append(outline);}
    let previous: Node = answer.layers.length ? views.get(answer.layers.at(-1)!.id)!.node : backing;
    for (const node of [composition, grid, safe, guides, multi, selection]) {
      if (previous.nextSibling !== node) zone.insertBefore(node, previous.nextSibling);
      previous = node;
    }
    paintEmpty(answer.layers.length === 0 && !answer.background);
    paintSelection(selectedId);
  }

  /**
   * "Does this box cross the zone edge" — the platform's one implementation of
   * that rule (`boxOverhangs`), not a third copy. The designer used to carry
   * two of its own and they disagreed about a rotated box.
   */
  function overhangsZone(box: DesignBox): boolean {
    return boxOverhangs(cfg.sizeMm, box);
  }

  /**
   * The empty zone is the quick start: three big choices a child can read —
   * Slika, Tekst, Slika i tekst — each of which runs the whole little flow
   * (pick, type, laid out). The catalogue and the icons stay one tap away
   * underneath for everyone who wants more.
   */
  function paintEmpty(isEmpty: boolean): void {
    empty.hidden = !isEmpty;
    if (!isEmpty || empty.childElementCount) return;
    const title = el('strong', undefined, ctx.startFlow ? ctx.texts.startTitle : cfg.placeholder);
    const big = el('div', 'pzd-start-row');
    const start = (icons: IconName[], label: string, hint: string, kind: 'photo' | 'text' | 'photo-text', allowed: boolean): void => {
      if (!allowed || !ctx.startFlow) return;
      const button = el('button', `pzd-start-card is-${kind}`);
      button.type = 'button';
      const art = el('span', 'pzd-start-art');
      art.innerHTML = icons.map((icon) => iconSvg(icon, 30)).join('<i>+</i>');
      button.append(art, el('strong', undefined, label), el('small', undefined, hint));
      button.addEventListener('click', () => ctx.startFlow?.(kind));
      big.appendChild(button);
    };
    start(['image'], ctx.texts.startPhoto, ctx.texts.startPhotoHint, 'photo', cfg.allow.image);
    start(['text'], ctx.texts.startText, ctx.texts.startTextHint, 'text', cfg.allow.text);
    start(['image', 'text'], ctx.texts.startBoth, ctx.texts.startBothHint, 'photo-text', cfg.allow.image && cfg.allow.text);
    const row = el('div', 'pzd-empty-row');
    const add = (icon: IconName, label: string, tool: 'text' | 'image' | 'icons' | 'templates', allowed: boolean): void => {
      if (!allowed) return;
      const button = el('button', 'pzd-empty-btn');
      button.type = 'button';
      button.innerHTML = iconSvg(icon, 18);
      button.appendChild(el('span', undefined, label));
      button.addEventListener('click', () => ctx.openTool(tool));
      row.appendChild(button);
    };
    add('template', ctx.texts.startTemplates, 'templates', true);
    add('artwork', ctx.texts.startIcons, 'icons', cfg.allow.artwork || cfg.allow.emoji);
    if (!big.childElementCount) {
      add('text', ctx.texts.addText, 'text', cfg.allow.text);
      add('image', ctx.texts.addImage, 'image', cfg.allow.image);
    }
    empty.append(title, ...(big.childElementCount ? [big] : [el('span', undefined, ctx.texts.zoneEmptyHint)]), row);
  }

  function paintBadge(view: LayerView, layer: DesignLayer): void {
    const problems = ctx.problemsFor(layer.id);
    const quality = layer.kind === 'image' ? qualityOf(layer) : undefined;
    const message = problems.length
      ? problemText(problems[0]!.code)
      : quality === 'low' ? ctx.texts.qualityLow : undefined;
    if (!message) {
      view.badge?.remove();
      view.badge = undefined;
      return;
    }
    const badge = view.badge ?? el('div', 'pzd-layer-badge');
    badge.className = `pzd-layer-badge${problems.length ? '' : ' is-warn'}`;
    badge.textContent = message;
    view.badge = badge;
    view.node.appendChild(badge);
  }

  function problemText(code: string): string {
    // The panel shows the full sentence; the badge only has room for a hint.
    if (code === 'low-resolution') return ctx.texts.qualityBlocked;
    if (code === 'outside-zone') return ctx.texts.outside;
    if (code === 'text-empty') return ctx.texts.textPlaceholder;
    return ctx.texts.fixFirst;
  }

  function qualityOf(layer: DesignLayer): ReturnType<typeof qualityFor> | undefined {
    if (layer.kind !== 'image' || !layer.sourcePx) return undefined;
    return qualityFor(effectiveDpi(layer.sourcePx, layer.box, layer.crop, layer.fit), cfg);
  }

  // ── Pictures ──────────────────────────────────────────────────────────────

  const queue: string[] = [];
  let pumping = false;

  function schedulePaint(layer: DesignLayer): void {
    const view = views.get(layer.id);
    if (!view || drag) return;
    const hash = layerContentHash(layer);
    const key = `${round(scale, 3)}x${dpr()}`;
    if (view.hash === hash && view.scaleKey === key) return;
    if (!queue.includes(layer.id)) queue.push(layer.id);
    if (!pumping) void pump();
  }

  function dpr(): number {
    const raw = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
    return Math.min(DPR_CAP, Math.max(1, raw));
  }

  /**
   * One layer at a time. Skia is single-threaded on the main thread, so
   * firing ten rasterisations at once would only queue them behind each other
   * while blocking the drag the shopper is in the middle of.
   */
  async function pump(): Promise<void> {
    if (pumping) return;
    pumping = true;
    try {
      while (queue.length && !stopped) {
        const id = queue.shift()!;
        const layer = ctx.store.state().answer.layers.find((l) => l.id === id);
        const view = views.get(id);
        if (!layer || !view) continue;
        await paintLayer(view, layer);
      }
    } finally {
      pumping = false;
    }
  }

  async function paintLayer(view: LayerView, layer: DesignLayer): Promise<void> {
    const hash = layerContentHash(layer);
    const key = `${round(scale, 3)}x${dpr()}`;
    view.padMm = layerPadMm(layer);

    if (layer.kind === 'text') {
      await ctx.fonts.ensure(layer.fontId).catch(() => undefined);
      await measureText(view, layer);
    }
    // Loading a face and measuring a line are both awaits, and measuring
    // REWRITES the box. Anything drawn from the layer as it was before that is
    // a picture of the wrong size — and fitting the box to such a picture is
    // how a text box gets stuck too small for its own words. The newer layer
    // is already queued, so hand over.
    const live = ctx.store.state().answer.layers.find((item) => item.id === layer.id);
    if (drag || !live || layerContentHash(live) !== hash) return;
    if (layer.kind === 'image' || layer.kind === 'artwork') {
      if (!ctx.renderer.assetUrl(layer.assetId)) {
        drawMissing(view);
        view.hash = hash;
        view.scaleKey = key;
        return;
      }
    }

    const raw = ctx.renderer.renderRaw;
    if (!raw) {
      drawDraft(view, layer);
      stage.classList.add('is-draft');
      view.hash = hash;
      view.scaleKey = key;
      return;
    }

    const padMm = view.padMm;
    const scene = layerScene(ctx.rawConfig, forRender(layer), scale, padMm);
    let out: { pixels: Uint8Array; width: number; height: number } | undefined;
    try {
      out = await raw.call(ctx.renderer, scene, dpr());
    } catch {
      out = undefined;
    }
    if (stopped || drag || views.get(layer.id) !== view) return;
    const current = ctx.store.value().layers.find(item => item.id === layer.id);
    if (!current || layerContentHash(current) !== hash || key !== `${round(scale, 3)}x${dpr()}`) return;
    if (!out || out.width <= 0 || out.height <= 0) {
      drawDraft(view, layer);
      view.hash = hash;
      view.scaleKey = key;
      return;
    }

    const canvas = view.raster ?? el('canvas', 'pzd-raster');
    canvas.width = out.width;
    canvas.height = out.height;
    const context = canvas.getContext('2d');
    if (!context) {
      drawDraft(view, layer);
      return;
    }
    // Copied out of WASM memory: ImageData will not take a view on it.
    const bytes = new Uint8ClampedArray(out.pixels.byteLength);
    bytes.set(out.pixels);
    context.putImageData(new ImageData(bytes, out.width, out.height), 0, 0);
    canvas.style.width = `${round(scene.width, 2)}px`;
    canvas.style.height = `${round(scene.height, 2)}px`;
    if (view.raster !== canvas) {
      view.draft?.remove();
      view.draft = undefined;
      view.raster = canvas;
      view.node.appendChild(canvas);
    }
    view.hash = hash;
    view.scaleKey = key;
    view.tileUrl = layer.pattern ? canvas.toDataURL() : undefined;
    applyRaster(view, layer.box);
    paintPattern(view, layer);
    stage.classList.remove('is-draft');

    // A fixed-width box does not hug its ink: its width is the shopper's and
    // its height came from the paragraph, so re-fitting it to the pixels would
    // undo the wrap the moment it was drawn.
    if (layer.kind === 'text' && !isFixedTextBox(layer)) fitToInk(view, layer, out);
  }

  /** The layer as the renderer should see it: opacity is CSS on the node. */
  function forRender(layer: DesignLayer): DesignLayer {
    if (layer.opacity === undefined) return layer;
    const copy = { ...layer } as DesignLayer & { opacity?: number };
    delete copy.opacity;
    return copy;
  }

  function drawMissing(view: LayerView): void {
    view.raster?.remove();
    view.raster = undefined;
    const draft = view.draft ?? el('div');
    draft.className = 'pzd-draft-missing';
    draft.textContent = ctx.texts.loading;
    if (view.draft !== draft) {
      view.draft = draft;
      view.node.appendChild(draft);
    }
  }

  /**
   * The DOM stand-in, used before Skia is available (and if it ever fails).
   * It is deliberately close but not exact: an HTML outline is a
   * `-webkit-text-stroke`, not the renderer's grown path, and a bent line
   * shows straight. The canvas says `is-draft` while any of these is on
   * screen, and everything is repainted for real on `renderer.onReady`.
   */
  function drawDraft(view: LayerView, layer: DesignLayer): void {
    view.raster?.remove();
    view.raster = undefined;
    const draft = view.draft ?? el('div');
    draft.textContent = '';
    draft.removeAttribute('style');
    if (layer.kind === 'text') {
      draft.className = 'pzd-draft-text';
      const px = layer.sizeMm * scale;
      draft.style.font = `${px}px/${layer.lineSpacing ?? 1.15} ${ctx.fonts.cssFamily(layer.fontId)}, sans-serif`;
      draft.style.color = hexOf(layer.color) || '#000';
      draft.style.textAlign = layer.align ?? 'center';
      if (layer.tracking) draft.style.letterSpacing = `${(layer.tracking / 1000) * px}px`;
      if (layer.outline && layer.outline.width > 0) {
        const w = layer.outline.width * px;
        draft.style.setProperty('-webkit-text-stroke', `${w}px ${hexOf(layer.outline.color) || '#000'}`);
        draft.style.paintOrder = 'stroke fill';
      }
      if (layer.shadow) {
        const rad = (layer.shadow.angle * Math.PI) / 180;
        const off = (layer.shadow.offset / 100) * px;
        draft.style.textShadow =
          `${round(Math.cos(rad) * off, 1)}px ${round(Math.sin(rad) * off, 1)}px ` +
          `${round((layer.shadow.blur / 100) * px, 1)}px ${hexOf(layer.shadow.color) || '#000'}`;
      }
      draft.textContent = layer.uppercase ? layer.text.toLocaleUpperCase('sr-RS') : layer.text;
      if(layer.orientation==='vertical')draft.textContent=designerVerticalText(draft.textContent);
      // A fixed box wraps at its own width, and the stand-in has to wrap at the
      // same one: the draft is what a shopper sees for the first second on a
      // slow connection, and a column that only became a column once Skia
      // arrived would jump under their hand.
      if (isFixedTextBox(layer)) draft.classList.add('is-wrap');
    } else if (layer.kind === 'shape') {
      draft.className = 'pzd-draft-art';
      draft.innerHTML = draftShape(layer.shape, hexOf(layer.fill), hexOf(layer.stroke?.color), (layer.stroke?.widthMm ?? 0) * scale);
    } else if(layer.kind==='qr') {
      draft.className='pzd-draft-missing';draft.textContent='Priprema QR koda…';
    } else {
      const url = ctx.renderer.assetUrl(layer.assetId);
      draft.className = layer.kind === 'image' ? 'pzd-draft-img' : 'pzd-draft-art';
      const img = el('img');
      img.src = url ?? '';
      img.alt = '';
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = layer.kind === 'image' ? 'cover' : 'contain';
      if (layer.kind === 'image' && layer.effects?.length) {
        const filters: string[] = [];
        for (const effect of layer.effects) {
          if (effect.kind === 'grayscale') filters.push('grayscale(1)');
          if (effect.kind === 'sepia') filters.push('sepia(.75)');
          if (effect.kind === 'brightness') filters.push(`brightness(${1 + effect.amount})`);
          if (effect.kind === 'contrast') filters.push(`contrast(${1 + effect.amount})`);
        }
        if (filters.length) img.style.filter = filters.join(' ');
      }
      draft.appendChild(img);
    }
    if (layer.flipX || layer.flipY) {
      draft.style.transform = `scale(${layer.flipX ? -1 : 1}, ${layer.flipY ? -1 : 1})`;
    }
    if (view.draft !== draft) {
      view.draft = draft;
      view.node.appendChild(draft);
    }
  }

  function draftShape(kind: string, fill: string, stroke: string, strokePx: number): string {
    const def = shapeDef(kind);
    const rule = def.fillRule ? ` fill-rule="${def.fillRule}"` : '';
    const strokeAttrs = stroke && strokePx > 0
      ? ` stroke="${stroke}" stroke-width="${round(strokePx, 2)}" stroke-linejoin="round" vector-effect="non-scaling-stroke"`
      : '';
    return (
      `<svg viewBox="0 0 1 1" preserveAspectRatio="none" width="100%" height="100%" aria-hidden="true">` +
      `<path d="${shapePath(kind)}" fill="${fill || 'none'}"${rule}${strokeAttrs}/></svg>`
    );
  }

  // ── Text measurement and ink fitting ──────────────────────────────────────

  let measureCanvas: HTMLCanvasElement | undefined;

  function fallbackEm(layer: DesignTextLayer): (line: string) => number {
    measureCanvas ??= el('canvas');
    const context = measureCanvas.getContext('2d');
    if (!context) return (line) => [...line].length * 0.5;
    context.font = `${MEASURE_PX}px "${ctx.fonts.cssFamily(layer.fontId)}", sans-serif`;
    return (line) => {
      try {
        return context.measureText(line).width / MEASURE_PX;
      } catch {
        return [...line].length * 0.5;
      }
    };
  }

  /**
   * `textWidthMm` — the straight advance width — travels inside the answer
   * because expansion is pure and synchronous: the print renderer has the
   * fonts but no chance to measure before it lays a bent line out. Skia is
   * asked where it can be; a 2D canvas answers where it cannot.
   */
  async function measureText(view: LayerView, layer: DesignTextLayer): Promise<void> {
    const key = layerFitKey(layer);
    if (view.measureKey === key) return;
    if (isFixedTextBox(layer)) {
      await measureFixedBox(view, layer, key);
      return;
    }
    let widthMm: number | undefined;
    const measure = ctx.renderer.measureText;
    if (measure) {
      // Measured in a box far wider than the text can need, and straight.
      // Asking inside the CURRENT box would measure the wrapped or clipped
      // layout instead of the advance width — and since the box is then fitted
      // to that measurement, a box once too small would stay too small for
      // ever, clipping every letter the shopper typed after it.
      const roomy: DesignTextLayer = {
        ...layer,
        bend: 0,
        box: {
          ...layer.box,
          w: Math.max(layer.box.w, layer.sizeMm * ([...layer.text].length + 2) * 1.4 + 10),
          h: Math.max(layer.box.h, layer.sizeMm * (textLines(layer.orientation==='vertical'?designerVerticalText(layer.text):layer.text).length + 1) * 2),
        },
      };
      const measureScale = 10; // Physical text measurement must not depend on a changing viewport.
      const scene = layerScene(ctx.rawConfig, roomy, measureScale, view.padMm);
      try {
        const out = await measure.call(ctx.renderer, scene, expandedLayerId('layer', layer.id));
        if (out && out.width > 0) widthMm = out.width / measureScale;
      } catch {
        widthMm = undefined;
      }
    }
    if (stopped || drag || ctx.store.value().layers.find(item => item.id === layer.id) !== layer) return;
    widthMm ??= textAdvanceMm(textLines(layer.orientation==='vertical'?designerVerticalText(layer.text):layer.text, layer.uppercase), layer.sizeMm, layer.tracking, fallbackEm(layer));
    view.measureKey = key;
    view.fitKey = key;
    view.fits = 0;
    view.grows = 0;
    view.fittedTo = undefined;
    const size = textBoxSize(layer, widthMm);
    // Width grows both ways from the centre (an auto box is centred on its own
    // words); height grows away from the anchored edge, so a top-anchored line
    // that becomes two keeps its first line exactly where it was.
    const box: DesignBox = anchorBoxHeight({ ...layer.box, w: size.w }, size.h, anchorVOf(layer));
    const sameWidth = Math.abs((layer.textWidthMm ?? 0) - widthMm) < 0.05;
    if (sameWidth && !boxDiffers(layer.box, box)) return;
    ctx.dispatch({
      type: 'update',
      id: layer.id,
      patch: { textWidthMm: round(widthMm), box },
      silent: true,
    });
  }

  /**
   * The height of a FIXED-width box: the paragraph the renderer lays out at
   * that width, in millimetres.
   *
   * This is the print, not an approximation of it. `measureText` runs the same
   * paragraph builder and the same fonts the press will, wrapped at the same
   * column, and reports the extent of the lines it produced — so the box the
   * shopper drags around is exactly the ink that will be set. The fallback
   * (a greedy wrap and `fixedTextBoxHeightMm`) is for a designer that is up
   * before the render bundle is; it is replaced by the real figure on the
   * first repaint after `renderer.onReady`.
   *
   * The width is NEVER changed here — that is the shopper's column. Only the
   * height moves, and it moves away from the anchored edge.
   */
  async function measureFixedBox(view: LayerView, layer: DesignTextLayer, key: string): Promise<void> {
    const measureScale = 10; // Physical text measurement must not depend on a changing viewport.
    let lines: number | undefined;
    const measure = ctx.renderer.measureText;
    if (measure) {
      // Measured in the layer's OWN box, unlike the hugging path's roomy one:
      // the wrap is the whole point, and a wider box would wrap somewhere else.
      const scene = layerScene(ctx.rawConfig, layer, measureScale, view.padMm);
      try {
        const out = await measure.call(ctx.renderer, scene, expandedLayerId('layer', layer.id));
        if (out && out.height > 0) lines = linesFromHeightMm(out.height / measureScale, layer.sizeMm, layer.lineSpacing);
      } catch {
        lines = undefined;
      }
    }
    // Before the render bundle is up, a greedy wrap of our own. It is replaced
    // by the measured figure on the first repaint after `renderer.onReady`.
    if (stopped || drag || ctx.store.value().layers.find(item => item.id === layer.id) !== layer) return;
    lines ??= wrapLineCount(layer.text, layer.box.w, layer.sizeMm, layer.tracking, fallbackEm(layer), layer.uppercase);
    // No padding term: the box IS the paragraph, which is what makes the anchor
    // exact — a top-anchored column whose box carried half a millimetre of slack
    // would set its first line half a millimetre below the edge the shopper
    // aligned it to, and "Sidro dole" would leave a strip of nothing under it.
    // `textWidthMm` is deliberately NOT written: that width is the shopper's.
    const height = fixedBoxHeightMm(lines, layer.sizeMm, layer.lineSpacing);
    const next = anchorBoxHeight(layer.box, height, anchorVOf(layer));
    view.measureKey = key;
    view.fitKey = key;
    view.fits = 0;
    view.grows = 0;
    view.fittedTo = undefined;
    if (!boxDiffers(layer.box, next, 0.05)) return;
    ctx.dispatch({ type: 'update', id: layer.id, patch: { box: next }, silent: true });
  }

  /**
   * After the raster exists, the handles are moved onto the ink itself: a word
   * in a script font occupies far less of its em box than one in a slab, and a
   * bounding box that does not hug the letters feels broken to drag.
   */
  function fitToInk(view: LayerView, layer: DesignLayer, out: { pixels: Uint8Array; width: number; height: number }): void {
    if (layer.kind !== 'text') return;
    const key = layerFitKey(layer);
    if (view.fitKey !== key) {
      view.fitKey = key;
      view.fits = 0;
      view.grows = 0;
      view.fittedTo = undefined;
    }
    const ink = inkBounds(out.pixels, out.width, out.height);
    if (!ink) return;
    const pxPerMm = out.width / (layer.box.w + view.padMm * 2);
    if (!(pxPerMm > 0)) return;
    // Ink that reaches the raster's edge was CUT there, so it is not the size
    // of the text — fitting the box to it would shrink the box onto the crop
    // and lose the rest of the word. Grow instead, and let the next render
    // settle it.
    const clipped = ink.left <= 0 || ink.top <= 0 || ink.right >= out.width - 1 || ink.bottom >= out.height - 1;
    if (clipped) {
      // Growing has its own budget, so it can never spend the fit's: a box
      // that had to be grown still gets fitted to the ink afterwards.
      if (view.grows >= 4) return;
      const room = {
        // A paragraph's width is its measured layout column; widening it would
        // only leave the handles standing off the letters for good.
        w: autoTextLaysOutAtBoxWidth(layer) ? layer.box.w : Math.min(cfg.sizeMm.w * 3, layer.box.w * 1.4 + layer.sizeMm),
        h: Math.min(cfg.sizeMm.h * 3, layer.box.h * 1.25 + layer.sizeMm * 0.5),
      };
      if (!boxDiffers(layer.box, { ...layer.box, ...room }, 0.2)) return;
      view.grows += 1;
      ctx.dispatch({ type: 'box', id: layer.id, box: { ...layer.box, ...room }, silent: true });
      return;
    }
    // A paragraph is laid out at the box width, so only its height may hug
    // the ink; a narrower box would wrap the widest line inside a word.
    const next = autoTextLaysOutAtBoxWidth(layer)
      ? boxFromInkKeepingWidth(layer.box, ink, pxPerMm, view.padMm)
      : boxFromInk(layer.box, ink, pxPerMm, view.padMm);
    if (!boxDiffers(layer.box, next, 0.2)) return;
    // Converged: the fit is asking for the box it asked for last time, so the
    // ink is where it can be and the two are 0.2 mm apart for good.
    if (view.fittedTo && !boxDiffers(view.fittedTo, next, 0.05)) return;
    if (view.fits >= 6) return;
    view.fits += 1;
    view.fittedTo = next;
    ctx.dispatch({ type: 'box', id: layer.id, box: next, silent: true });
  }

  // ── Selection chrome ──────────────────────────────────────────────────────

  const handles = new Map<HandleId, HTMLButtonElement>();
  const stem = el('div', 'pzd-sel-stem');

  function buildSelection(): void {
    const make = (id: HandleId, className: string, label: string): void => {
      const button = el('button', className);
      button.type = 'button';
      button.dataset.handle = id;
      button.setAttribute('aria-label', label);
      button.tabIndex = -1;
      handles.set(id, button);
      selection.appendChild(button);
    };
    make('nw', 'pzd-handle', ctx.texts.scale);
    make('ne', 'pzd-handle', ctx.texts.scale);
    make('se', 'pzd-handle', ctx.texts.scale);
    make('sw', 'pzd-handle', ctx.texts.scale);
    make('n', 'pzd-handle is-side', ctx.texts.scale);
    make('s', 'pzd-handle is-side', ctx.texts.scale);
    make('e', 'pzd-handle is-side is-x', ctx.texts.scale);
    make('w', 'pzd-handle is-side is-x', ctx.texts.scale);
    selection.appendChild(stem);
    make('rot', 'pzd-handle is-rotate', ctx.texts.rotate);
    const place = (id: HandleId, left: string, top: string): void => {
      const node = handles.get(id);
      if (!node) return;
      node.style.left = left;
      node.style.top = top;
    };
    place('nw', '0%', '0%');
    place('ne', '100%', '0%');
    place('se', '100%', '100%');
    place('sw', '0%', '100%');
    place('n', '50%', '0%');
    place('s', '50%', '100%');
    place('e', '100%', '50%');
    place('w', '0%', '50%');
    const rot = handles.get('rot');
    if (rot) {
      rot.style.left = '50%';
      rot.style.top = '-26px';
    }
    stem.style.left = '50%';
    stem.style.top = '-26px';
    stem.style.height = '26px';
  }

  buildSelection();

  function paintSelection(selectedId: string | null): void {
    const layer = selectedId ? ctx.store.state().answer.layers.find((l) => l.id === selectedId) : undefined;
    if (!layer || ctx.isHidden(layer.id) || layer.visible===false || ctx.view.review) {
      selection.hidden = true;
      actions.hidden = true;
      return;
    }
    selection.hidden = false;
    selection.classList.toggle('is-locked', Boolean(layer.locked));
    applyGeometry(selection, layer.box);
    const sides = !ctx.view.lockAspect && !layer.groupId && (layer.kind === 'image' || layer.kind === 'shape');
    // A text layer gets the two SIDE handles and nothing else: dragging one
    // sets the wrap width (and makes an auto box a fixed one, which is the
    // reference editor's own rule), while the height is always the lines.
    const wrapWidth = canHaveFixedWidth(layer);
    for (const id of ['n', 's'] as HandleId[]) {
      const node = handles.get(id);
      if (node) node.hidden = !sides;
    }
    for (const id of ['e', 'w'] as HandleId[]) {
      const node = handles.get(id);
      if (node) node.hidden = !(sides || wrapWidth);
    }
    paintActions(layer);
  }

  /**
   * The floating mini toolbar: three pills above the selected object.
   *
   * Three, not six, and per kind — the reference editor's own rule. What goes
   * here is what a shopper reaches for WHILE looking at the thing itself
   * (retype it, recolour it, swap the picture); locking, ordering and deleting
   * are deliberate operations and live in the contextual toolbar and the
   * context menu, where an accidental click costs nothing.
   */
  /** The colour pill's own popover, so a recolour never leaves the canvas. */
  function colorPill(
    anchor: HTMLElement,
    title: string,
    value: Color | undefined,
    onPick: (color: Color | undefined) => void,
    allowNone = false,
  ): void {
    openPopover({
      ctx,
      anchor,
      title,
      className: 'is-color',
      build: (body) => {
        const block = createColorBlock({ ctx, value, allowNone, onPick });
        body.append(block.element);
        return () => block.destroy();
      },
    });
  }

  /** The font pill opens the same Fontovi list the toolbar does. */
  function fontPill(anchor: HTMLElement, layerId: string, fontId: string): void {
    openPopover({
      ctx,
      anchor,
      className: 'is-fonts',
      build: (body, handle) => {
        const picker = openFontPicker({
          ctx,
          fontId,
          host: body,
          onPick: (next) => {
            void ctx.fonts.ensure(next).catch(() => undefined);
            ctx.dispatch({ type: 'update', id: layerId, patch: { fontId: next } });
          },
          onClose: () => handle.close(),
        });
        return () => picker.destroy();
      },
    });
  }

  function miniActionsFor(layer: DesignLayer): Array<{
    icon: IconName;
    label: string;
    go: (anchor: HTMLElement) => void;
    off?: boolean;
  }> {
    switch (layer.kind) {
      case 'text':
        return [
          { icon: 'text', label: ctx.texts.editText, go: () => ctx.openTool('text') },
          {
            icon: 'opacity',
            label: ctx.texts.color,
            go: (anchor) => colorPill(anchor, ctx.texts.textColor, layer.color, (color) => {
              if (color) ctx.dispatch({ type: 'update', id: layer.id, patch: { color, styleId: undefined } });
            }),
          },
          { icon: 'magic', label: ctx.texts.font, go: (anchor) => fontPill(anchor, layer.id, layer.fontId) },
        ];
      case 'image':
        return [
          { icon: 'swap', label: ctx.texts.changeImage, go: () => { startReplace(ctx, layer.id); ctx.openTool('image'); } },
          { icon: 'crop', label: ctx.texts.cropTool, go: () => ctx.openTool('image') },
          {
            icon: 'magic',
            label: ctx.texts.removeBackground,
            go: () => void toggleRemoveBackground(ctx, layer),
            off: !ctx.config.image.allowRemoveBackground || !ctx.uploads.removeBackground,
          },
        ];
      case 'shape':
        return [
          {
            icon: 'opacity',
            label: ctx.texts.fill,
            go: (anchor) => colorPill(anchor, ctx.texts.fillColor, layer.fill, (color) => {
              ctx.dispatch({ type: 'update', id: layer.id, patch: { fill: color } });
            }, Boolean(layer.stroke)),
          },
          {
            icon: 'outline',
            label: ctx.texts.stroke,
            go: (anchor) => colorPill(anchor, ctx.texts.lineColor, layer.stroke?.color, (color) => {
              ctx.dispatch({
                type: 'update',
                id: layer.id,
                patch: color
                  ? { stroke: { color, widthMm: layer.stroke?.widthMm || 0.5 } }
                  : { stroke: undefined, ...(layer.fill ? {} : { fill: ctx.config.shape.palette[0] ?? { hex: '#000000' } }) },
              });
            }, true),
          },
          { icon: 'trash', label: ctx.texts.remove, go: () => run(ctx, { op: 'delete', ids: [layer.id] }), off: !canDelete(layer) },
        ];
      case 'qr':
        return [
          { icon: 'qr', label: ctx.texts.editQr, go: () => ctx.openTool('elements') },
          { icon: 'trash', label: ctx.texts.remove, go: () => run(ctx, { op: 'delete', ids: [layer.id] }), off: !canDelete(layer) },
        ];
      default:
        return [
          { icon: 'opacity', label: ctx.texts.color, go: () => ctx.openTool('icons') },
          { icon: 'copy', label: ctx.texts.duplicate, go: () => run(ctx, { op: 'duplicate', ids: [layer.id] }) },
          { icon: 'trash', label: ctx.texts.remove, go: () => run(ctx, { op: 'delete', ids: [layer.id] }), off: !canDelete(layer) },
        ];
    }
  }

  function paintActions(layer: DesignLayer): void {
    actions.hidden = Boolean(drag) || !ctx.view.miniToolbar || ctx.view.review || (ctx.phone() && stage.clientHeight < 240);
    actions.textContent = '';
    for (const action of miniActionsFor(layer)) {
      const button = el('button', 'pzd-pill-btn');
      button.type = 'button';
      button.innerHTML = iconSvg(action.icon, 16);
      button.append(el('span', undefined, action.label));
      button.setAttribute('aria-label', action.label);
      button.title = action.label;
      button.disabled = action.off === true;
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        // The pill acts on the layer it is floating over, so make sure that is
        // the one the panels will show.
        if (ctx.store.state().selectedId !== layer.id) ctx.dispatch({ type: 'select', id: layer.id });
        action.go(button);
      });
      actions.appendChild(button);
    }

    // Placed in the stage, not the zone, so it can be kept on screen even when
    // the layer itself sits in a corner or hangs over the edge.
    const half = Math.hypot(layer.box.w, layer.box.h) / 2;
    const cx = zoneLeft + layer.box.x * scale;
    const below = zoneTop + (layer.box.y + half) * scale + 14;
    const above = zoneTop + (layer.box.y - half) * scale - 46;
    const height = 40;
    const fits = below + height < stage.clientHeight;
    const top = clamp(fits ? below : above, 4, Math.max(4, stage.clientHeight - height - 4));
    actions.style.left = `${Math.round(clamp(cx, 90, Math.max(90, stage.clientWidth - 90)))}px`;
    actions.style.top = `${Math.round(top)}px`;
    actions.style.transform = 'translateX(-50%)';
  }

  function showGuides(list: SnapGuide[]): void {
    while (guideNodes.length < list.length) {
      const node = el('div', 'pzd-guide');
      guideNodes.push(node);
      guides.appendChild(node);
    }
    guideNodes.forEach((node, index) => {
      const guide = list[index];
      if (!guide) {
        node.hidden = true;
        return;
      }
      node.hidden = false;
      node.className = `pzd-guide is-${guide.axis}`;
      if (guide.axis === 'x') {
        node.style.left = `${round(guide.at * scale, 1)}px`;
        node.style.top = '';
      } else {
        node.style.top = `${round(guide.at * scale, 1)}px`;
        node.style.left = '';
      }
    });
  }

  function clearGuides(): void {
    for (const node of guideNodes) node.hidden = true;
  }

  // ── Gestures ──────────────────────────────────────────────────────────────

  function layerAt(clientX: number, clientY: number): DesignLayer | undefined {
    const point = pointToMm(clientX, clientY);
    const skip = new Set<string>();
    for (const layer of ctx.store.state().answer.layers) {
      // Hidden while working, or authored as "no interaction": both are drawn
      // and printed, and neither can be picked up.
      if (ctx.isHidden(layer.id) || !canEdit(layer)) skip.add(layer.id);
    }
    // A finger is not a pixel: a 3 mm tolerance makes thin text and hairline
    // shapes grabbable without a pinch-zoom first.
    const tol = ctx.phone() ? 3 : 1;
    const id = hitTest(ctx.store.state().answer, point, { tolMm: tol, skip, sizeMm:cfg.sizeMm });
    return id ? ctx.store.state().answer.layers.find((l) => l.id === id) : undefined;
  }

  function beginDrag(event: PointerEvent, layer: DesignLayer, handle?: HandleId): void {
    const rect = zone.getBoundingClientRect();
    const kind = handle === 'rot' ? 'rotate'
      : handle && SIDE_HANDLES.has(handle) ? 'side'
        : handle ? 'corner' : 'move';
    const centre = { x: rect.left + layer.box.x * scale, y: rect.top + layer.box.y * scale };
    drag = {
      kind,
      id: layer.id,
      handle,
      startBox: { ...layer.box },
      ...(layer.kind === 'text' ? { startSizeMm: layer.sizeMm } : {}),
      live: { ...layer.box },
      rect: { left: rect.left, top: rect.top },
      pointers: new Map([[event.pointerId, { x: event.clientX, y: event.clientY }]]),
      startPointer: { x: event.clientX, y: event.clientY },
      startCentre: centre,
      startAngle: Math.atan2(event.clientY - centre.y, event.clientX - centre.x),
      startDist: 0,
      startMid: { x: event.clientX, y: event.clientY },
      shift: event.shiftKey,
      moved: false,
      others: ctx.store.state().answer.layers.filter((l) => l.id !== layer.id && !ctx.isHidden(l.id)),
    };
    actions.hidden = true;
    try {
      zone.setPointerCapture(event.pointerId);
    } catch {
      /* capture is a nicety; the document listeners below still finish it */
    }
  }

  function onPointerDown(event: PointerEvent): void {
    if(ctx.view.review||ctx.view.panMode||space)return;
    if (event.button !== undefined && event.button > 0) return;
    const target = event.target as HTMLElement | null;
    const handle = target?.dataset?.handle as HandleId | undefined;
    const selected = ctx.selected();

    if (handle && selected && !selected.locked) {
      event.preventDefault();
      beginDrag(event, selected, handle);
      return;
    }

    // A second finger on an already-dragging layer turns the move into a pinch.
    if (drag && drag.kind === 'move' && drag.pointers.size === 1) {
      const first = [...drag.pointers.values()][0]!;
      drag.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      drag.kind = 'pinch';
      drag.startDist = Math.hypot(event.clientX - first.x, event.clientY - first.y);
      drag.startAngle = Math.atan2(event.clientY - first.y, event.clientX - first.x);
      drag.startMid = { x: (first.x + event.clientX) / 2, y: (first.y + event.clientY) / 2 };
      drag.startBox = { ...drag.live };
      const layer = ctx.selected();
      if (layer?.kind === 'text') drag.startSizeMm = drag.liveSizeMm ?? layer.sizeMm;
      event.preventDefault();
      return;
    }

    const layer = layerAt(event.clientX, event.clientY);
    if (!layer) {
      // Empty canvas: start a rubber band. It only becomes a selection once
      // the pointer has actually travelled, so a plain click still deselects.
      ctx.selection.clear();
      editingGroup = undefined;
      ctx.dispatch({ type: 'select', id: null });
      band = { pointer: event.pointerId, startX: event.clientX, startY: event.clientY };
      try {
        zone.setPointerCapture(event.pointerId);
      } catch {
        /* the document listeners still finish the band */
      }
      sync();
      return;
    }
    event.preventDefault();
    if(event.ctrlKey||event.metaKey||event.shiftKey){if(ctx.selection.has(layer.id))ctx.selection.delete(layer.id);else ctx.selection.add(layer.id);ctx.dispatch({type:'select',id:ctx.selection.has(layer.id)?layer.id:[...ctx.selection].at(-1)??null});ctx.refreshPanel();sync();return;}
    // Inside an open group every member is its own element; outside one, a
    // click anywhere on the group takes the whole group — which is what the
    // command layer does with `groupId` on the other side.
    if (layer.groupId && layer.groupId !== editingGroup) {
      ctx.selection.clear();
      for (const member of ctx.store.value().layers) {
        if (member.groupId === layer.groupId) ctx.selection.add(member.id);
      }
    } else {
      ctx.selection.clear();
      ctx.selection.add(layer.id);
    }
    if (layer.id !== ctx.store.state().selectedId) ctx.dispatch({ type: 'select', id: layer.id });

    const now = Date.now();
    if (ctx.phone() && lastTap.id === layer.id && now - lastTap.at < 320) {
      lastTap = { id: '', at: 0 };
      if (layer.groupId && layer.groupId !== editingGroup) {
        // Double-click a group: edit inside it.
        editingGroup = layer.groupId;
        ctx.selection.clear();
        ctx.selection.add(layer.id);
        sync();
        return;
      }
      options.onOpenLayer(layer);
      return;
    }
    lastTap = { id: layer.id, at: now };

    if (longPress !== undefined) window.clearTimeout(longPress);
    longPress = window.setTimeout(() => {
      longPress = undefined;
      if (drag?.moved) return;
      // A long press is the touch equivalent of a right-click, which is what
      // the reference editor does on a phone too.
      if (!ctx.phone()) openContextMenu({ ctx, at: { clientX: event.clientX, clientY: event.clientY }, layer, anchor: zone });
    }, 520);

    if (!layer.locked) beginDrag(event, layer);
  }

  /** The rubber band, in stage coordinates, while a drag on empty canvas runs. */
  function paintBand(event: PointerEvent): void {
    if (!band) return;
    const rect = stage.getBoundingClientRect();
    const left = Math.min(band.startX, event.clientX) - rect.left;
    const top = Math.min(band.startY, event.clientY) - rect.top;
    const width = Math.abs(event.clientX - band.startX);
    const height = Math.abs(event.clientY - band.startY);
    marquee.hidden = width < 3 && height < 3;
    marquee.style.left = `${Math.round(left)}px`;
    marquee.style.top = `${Math.round(top)}px`;
    marquee.style.width = `${Math.round(width)}px`;
    marquee.style.height = `${Math.round(height)}px`;
  }

  /** Everything the band touched, in zone millimetres. */
  function finishBand(event: PointerEvent): void {
    if (!band) return;
    const dragged = Math.abs(event.clientX - band.startX) > 3 || Math.abs(event.clientY - band.startY) > 3;
    const start = pointToMm(band.startX, band.startY);
    const end = pointToMm(event.clientX, event.clientY);
    band = undefined;
    marquee.hidden = true;
    if (!dragged) return;
    const left = Math.min(start.x, end.x);
    const right = Math.max(start.x, end.x);
    const top = Math.min(start.y, end.y);
    const bottom = Math.max(start.y, end.y);
    ctx.selection.clear();
    for (const layer of ctx.store.value().layers) {
      if (ctx.isHidden(layer.id) || !canEdit(layer) || layer.visible === false) continue;
      const extent = boxExtent(layer.box);
      if (extent.right < left || extent.left > right || extent.bottom < top || extent.top > bottom) continue;
      ctx.selection.add(layer.id);
    }
    ctx.dispatch({ type: 'select', id: [...ctx.selection].at(-1) ?? null });
    ctx.refreshPanel();
    sync();
  }

  function onPointerMove(event: PointerEvent): void {
    if (band) {
      paintBand(event);
      return;
    }
    if (!drag) return;
    if (!drag.pointers.has(event.pointerId)) return;
    drag.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    drag.shift = event.shiftKey;
    const view = views.get(drag.id);
    if (!view) return;
    const layer = ctx.store.state().answer.layers.find((l) => l.id === drag!.id);
    if (!layer) return;

    const moved = Math.hypot(event.clientX - drag.startPointer.x, event.clientY - drag.startPointer.y);
    if (moved > 4) {
      drag.moved = true;
      if (longPress !== undefined) {
        window.clearTimeout(longPress);
        longPress = undefined;
      }
    }

    let next = drag.startBox;
    let guideList: SnapGuide[] = [];
    let sizeMm = drag.startSizeMm;

    if (drag.kind === 'pinch' && drag.pointers.size >= 2) {
      const [a, b] = [...drag.pointers.values()];
      if (a && b) {
        const dist = Math.hypot(b.x - a.x, b.y - a.y);
        const angle = Math.atan2(b.y - a.y, b.x - a.x);
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const k = drag.startDist > 8 ? clamp(dist / drag.startDist, 0.1, 12) : 1;
        const spin = ((angle - drag.startAngle) * 180) / Math.PI;
        next = {
          ...drag.startBox,
          x: drag.startBox.x + (mid.x - drag.startMid.x) / scale,
          y: drag.startBox.y + (mid.y - drag.startMid.y) / scale,
          w: Math.max(1, drag.startBox.w * k),
          h: Math.max(1, drag.startBox.h * k),
          rotation: snapAngle((drag.startBox.rotation ?? 0) + spin, false),
        };
        if (drag.startSizeMm) sizeMm = Math.max(0.5, drag.startSizeMm * k);
      }
    } else if (drag.kind === 'move') {
      next = {
        ...drag.startBox,
        x: drag.startBox.x + (event.clientX - drag.startPointer.x) / scale,
        y: drag.startBox.y + (event.clientY - drag.startPointer.y) / scale,
      };
      const snapped = ctx.view.snap?snapBox(cfg, next, { others: drag.others }):{box:next,guides:[]};
      next = snapped.box;
      guideList = snapped.guides;
    } else if (drag.kind === 'rotate') {
      const angle = Math.atan2(event.clientY - drag.startCentre.y, event.clientX - drag.startCentre.x);
      const spin = ((angle - drag.startAngle) * 180) / Math.PI;
      next = { ...drag.startBox, rotation: snapAngle((drag.startBox.rotation ?? 0) + spin, drag.shift) };
    } else {
      const local = toLocal(event.clientX, event.clientY, drag);
      const hw = drag.startBox.w / 2;
      const hh = drag.startBox.h / 2;
      if (drag.kind === 'corner') {
        const sx = drag.handle === 'ne' || drag.handle === 'se' ? 1 : -1;
        const sy = drag.handle === 'se' || drag.handle === 'sw' ? 1 : -1;
        const dx = sx * hw, dy = sy * hh;
        const dot = dx * dx + dy * dy;
        const k = dot > 0 ? clamp((local.x * dx + local.y * dy) / dot, 0.06, 14) : 1;
        const shift = rotate({ x: dx * (k - 1), y: dy * (k - 1) }, drag.startBox.rotation ?? 0);
        next = {
          ...drag.startBox,
          x: drag.startBox.x + shift.x,
          y: drag.startBox.y + shift.y,
          w: Math.max(0.5, drag.startBox.w * k),
          h: Math.max(0.5, drag.startBox.h * k),
        };
        if (drag.startSizeMm) sizeMm = Math.max(0.5, drag.startSizeMm * k);
      } else if (layer.kind === 'text' && (drag.handle === 'e' || drag.handle === 'w')) {
        // The wrap column: width only, and `sizeMm` deliberately untouched —
        // a side handle re-flows the words, a corner one makes them bigger.
        next = sideWidthBox(drag.startBox, local.x, drag.handle, minWrapWidthMm(layer.sizeMm));
      } else {
        let w = drag.startBox.w, h = drag.startBox.h, ox = 0, oy = 0;
        if (drag.handle === 'e') { w = Math.max(0.5, local.x + hw); ox = (local.x - hw) / 2; }
        if (drag.handle === 'w') { w = Math.max(0.5, hw - local.x); ox = (local.x + hw) / 2; }
        if (drag.handle === 's') { h = Math.max(0.5, local.y + hh); oy = (local.y - hh) / 2; }
        if (drag.handle === 'n') { h = Math.max(0.5, hh - local.y); oy = (local.y + hh) / 2; }
        const shift = rotate({ x: ox, y: oy }, drag.startBox.rotation ?? 0);
        next = { ...drag.startBox, x: drag.startBox.x + shift.x, y: drag.startBox.y + shift.y, w, h };
      }
      const snapped = ctx.view.snap?snapBox(cfg, next, { others: drag.others, centre: false }):{box:next,guides:[]};
      next = snapped.box;
      guideList = snapped.guides;
    }

    if (layer.groupId) {
      try {
        const grouped = applyDesignerCommand(ctx.rawConfig, ctx.store.value(), { op: 'place', ids: [layer.id], box: next });
        for (const member of grouped.layers.filter(l => l.groupId === layer.groupId && l.id !== layer.id)) {
          const memberView = views.get(member.id);
          if (memberView) { applyGeometry(memberView.node, member.box); applyRaster(memberView, member.box); paintPattern(memberView, member); }
        }
      } catch { return; }
    }
    // The contour follows the drag. `stickerContourPreview` is allowed a much
    // lower resolution than the print path (≥ 2 px/mm), which is what makes a
    // live shape affordable at all; the full-resolution backing is rebuilt
    // once the pointer lifts. Only one recomputation is ever in flight.
    if (ctx.store.value().sticker) {
      stickerStatus.hidden = false;
      stickerStatus.textContent = ctx.texts.stickerDuringDrag;
    }
    drag.live = next;
    drag.liveSizeMm = sizeMm;
    applyGeometry(view.node, next);
    // Stretching a text raster sideways would show the letters themselves
    // getting wider, which is the one thing a wrap column does NOT do. The
    // outline follows the pointer, the words re-flow on release — the widget's
    // standing gesture rule (CSS during, one render after).
    const wrapDrag = drag.kind === 'side' && layer.kind === 'text'
      && (drag.handle === 'e' || drag.handle === 'w');
    if (!wrapDrag) applyRaster(view, next);
    if (layer.pattern) paintPattern(view, { ...layer, box: next });
    applyGeometry(selection, next);
    showGuides(ctx.view.guidelines?guideList:[]);
    if (cfg.boundsMode === 'inside') {
      const bad = overhangsZone(next);
      view.node.classList.toggle('is-outside', bad);
      if (bad && Date.now() - outsideWarnedAt > 4000) {
        outsideWarnedAt = Date.now();
        ctx.toast(ctx.texts.outside, 'info');
      }
    }
  }

  /** The pointer in the box's own unrotated frame, millimetres from its centre. */
  function toLocal(clientX: number, clientY: number, active: Drag): { x: number; y: number } {
    const dx = (clientX - active.startCentre.x) / scale;
    const dy = (clientY - active.startCentre.y) / scale;
    return rotate({ x: dx, y: dy }, -(active.startBox.rotation ?? 0));
  }

  function rotate(point: { x: number; y: number }, degrees: number): { x: number; y: number } {
    const rad = (degrees * Math.PI) / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    return { x: point.x * cos - point.y * sin, y: point.x * sin + point.y * cos };
  }

  function onPointerUp(event: PointerEvent): void {
    if (band && band.pointer === event.pointerId) {
      if (event.type === 'pointercancel' || event.type === 'lostpointercapture') {
        band = undefined;
        marquee.hidden = true;
        return;
      }
      finishBand(event);
      return;
    }
    if (longPress !== undefined && !drag?.moved) {
      // A tap, not a press: let the long-press timer decide nothing.
      window.clearTimeout(longPress);
      longPress = undefined;
    }
    if (!drag) return;
    if (event.type === 'pointercancel' || event.type === 'lostpointercapture') {
      if (!drag.pointers.has(event.pointerId)) return;
      drag = undefined; backingKey = ''; clearGuides(); if (layoutPending) relayout(); sync(); return;
    }
    drag.pointers.delete(event.pointerId);
    if (drag.pointers.size > 0) return;
    const finished = drag;
    drag = undefined;
    if (layoutPending) relayout();
    backingKey = "";
    clearGuides();
    if (!finished.moved) {
      sync();
      return;
    }
    const tag = `${finished.kind}:${finished.id}`;
    const dragged = ctx.store.value().layers.find((item) => item.id === finished.id);
    // Dragging a side handle on a text layer IS how a column is made: the box
    // stops following the words and the words start following the box. One
    // step in the history — the shopper made one gesture.
    if (dragged?.kind === 'text' && finished.kind === 'side'
      && (finished.handle === 'e' || finished.handle === 'w')) {
      ctx.dispatch({
        type: 'update',
        id: finished.id,
        patch: { ...fixedByDragPatch(dragged), box: finished.live },
        tag,
      });
      sync();
      return;
    }
    if (finished.liveSizeMm !== undefined && finished.liveSizeMm !== finished.startSizeMm) {
      const size = clamp(round(finished.liveSizeMm, 2), cfg.text.minSizeMm, cfg.text.maxSizeMm);
      ctx.dispatch({ type: 'update', id: finished.id, patch: { box: finished.live, sizeMm: size }, tag });
    } else {
      ctx.dispatch({ type: 'box', id: finished.id, box: finished.live, tag });
    }
    sync();
  }

  // ── Keyboard ──────────────────────────────────────────────────────────────

  function editable(target: EventTarget | null): boolean {
    const node = target as HTMLElement | null;
    const tag = node?.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node?.isContentEditable === true
      || Boolean(node?.closest('button, [role=dialog], .pzd-ph-sub, .pzd-crop'));
  }

  /** The node the shopper actually typed into, before the shadow retargeted it. */
  function deepTarget(event: Event): EventTarget | null {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    return path[0] ?? event.target;
  }

  /**
   * Every shortcut in the "Prečice na tastaturi" dialog, and nothing else.
   *
   * The matching lives in `keyboard.ts` so the dialog and this handler read
   * the same table: a row the dialog promises but nothing implements, or a key
   * this steals without telling anyone, both become impossible.
   */
  function onKeyDown(event: KeyboardEvent): void {
    if (stopped || !ctx.root.isConnected) return;
    if (!eventInside(event, ctx.root) || editable(deepTarget(event))) return;
    if (event.code === 'Space') {
      space = true;
      event.preventDefault();
      return;
    }
    if (ctx.view.review) return;

    const shortcut = matchShortcut(event);
    if (!shortcut) return;
    const layer = ctx.selected();
    const ids = ctx.selection.size ? [...ctx.selection] : layer ? [layer.id] : [];
    const take = (): void => {
      event.preventDefault();
      event.stopPropagation();
    };

    switch (shortcut) {
      case 'undo':
        take();
        ctx.dispatch({ type: 'undo' });
        return;
      case 'redo':
        take();
        ctx.dispatch({ type: 'redo' });
        return;
      case 'zoomIn':
        take();
        ctx.setView({ zoom: nextZoom(ctx.view.zoom, 1) });
        return;
      case 'zoomOut':
        take();
        ctx.setView({ zoom: nextZoom(ctx.view.zoom, -1) });
        return;
      case 'zoomFit':
        take();
        ctx.setView({ zoom: 1, panMode: false });
        return;
      case 'selectAll':
        take();
        for (const item of ctx.store.value().layers) {
          if (canEdit(item)) ctx.selection.add(item.id);
        }
        ctx.dispatch({ type: 'select', id: [...ctx.selection].at(-1) ?? null });
        ctx.refreshPanel();
        sync();
        return;
      case 'addText':
        take();
        ctx.openTool('text');
        return;
      case 'copy':
        take();
        ctx.copySelection();
        return;
      case 'cut':
        take();
        ctx.copySelection();
        if (ids.length) run(ctx, { op: 'delete', ids });
        return;
      case 'paste':
        take();
        ctx.pasteSelection();
        return;
      case 'escape':
        take();
        // Leave the group first, then the selection: a second Escape is the
        // dialog's to close.
        if (editingGroup) {
          editingGroup = undefined;
          sync();
          return;
        }
        ctx.selection.clear();
        ctx.dispatch({ type: 'select', id: null });
        return;
      default:
        break;
    }

    if (!ids.length) return;
    switch (shortcut) {
      case 'delete':
        take();
        if (ids.every((id) => canDelete(ctx.store.value().layers.find((item) => item.id === id)))) {
          run(ctx, { op: 'delete', ids });
        } else {
          ctx.toast(ctx.texts.permanentRefused, 'error');
        }
        return;
      case 'duplicate':
        take();
        run(ctx, { op: 'duplicate', ids });
        return;
      case 'group':
        take();
        run(ctx, { op: 'group', ids });
        return;
      case 'ungroup':
        take();
        run(ctx, { op: 'ungroup', ids });
        return;
      case 'scaleUp':
      case 'scaleDown':
      case 'scaleUpFine':
      case 'scaleDownFine':
        take();
        run(ctx, { op: 'scale', ids, factor: SCALE_FACTORS[shortcut] });
        return;
      case 'nudge':
      case 'nudgeSmall':
      case 'nudgeBig': {
        const delta = arrowDelta(event.key);
        if (!delta) return;
        take();
        const step = nudgeStepMm(event);
        run(ctx, { op: 'move', ids, dx: delta.dx * step, dy: delta.dy * step });
        return;
      }
      default:
        return;
    }
  }

  /**
   * Ctrl+wheel zooms at the pointer, which is the gesture every design tool
   * has trained people to expect. The pan offset is corrected so the point
   * under the cursor stays under the cursor.
   */
  function onWheel(event: WheelEvent): void {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    const rect = zone.getBoundingClientRect();
    const before = { x: (event.clientX - rect.left) / scale, y: (event.clientY - rect.top) / scale };
    const factor = event.deltaY < 0 ? 1.1 : 1 / 1.1;
    const zoom = clampZoom(ctx.view.zoom * factor);
    if (Math.abs(zoom - ctx.view.zoom) < 0.001) return;
    const growth = zoom / ctx.view.zoom;
    panX -= before.x * scale * (growth - 1);
    panY -= before.y * scale * (growth - 1);
    ctx.setView({ zoom });
  }

  zone.addEventListener('pointerdown', onPointerDown);
  const onPanDown=(event:PointerEvent)=>{
    if(!(ctx.view.panMode||space)||event.button>0)return;
    event.preventDefault();event.stopPropagation();panning={pointer:event.pointerId,x:event.clientX,y:event.clientY,panX,panY};
    try{stage.setPointerCapture(event.pointerId);}catch{/* release also ends on cancel */}
  };
  const onPanMove=(event:PointerEvent)=>{if(!panning||panning.pointer!==event.pointerId)return;panX=panning.panX+event.clientX-panning.x;panY=panning.panY+event.clientY-panning.y;relayout();};
  const onPanEnd=(event:PointerEvent)=>{if(panning?.pointer!==event.pointerId)return;if(event.type==='pointercancel'){panX=panning.panX;panY=panning.panY;relayout();}panning=undefined;};
  stage.addEventListener('pointerdown',onPanDown,true);stage.addEventListener('pointermove',onPanMove);stage.addEventListener('pointerup',onPanEnd);stage.addEventListener('pointercancel',onPanEnd);stage.addEventListener('lostpointercapture',onPanEnd);
  const releaseSpace=(event:KeyboardEvent)=>{if(event.code==='Space')space=false;};
  document.addEventListener('keyup',releaseSpace,true);
  zone.addEventListener('pointermove', onPointerMove);
  zone.addEventListener('pointerup', onPointerUp);
  zone.addEventListener('pointercancel', onPointerUp);
  zone.addEventListener('lostpointercapture', onPointerUp);
  zone.addEventListener('dblclick', (event) => {
    if (ctx.phone()) return;
    const layer = layerAt(event.clientX, event.clientY);
    if (!layer) return;
    if (layer.groupId && layer.groupId !== editingGroup) {
      editingGroup = layer.groupId;
      ctx.selection.clear();
      ctx.selection.add(layer.id);
      sync();
      return;
    }
    if (layer.kind === 'image') openCropDialog(ctx, layer.id);
    else options.onOpenLayer(layer);
  });
  // Right-click anywhere on the stage: over a layer it acts on that layer,
  // over empty canvas it offers the two items that still apply.
  stage.addEventListener('contextmenu', (event) => {
    if (ctx.view.review) return;
    event.preventDefault();
    const layer = layerAt(event.clientX, event.clientY);
    if (!layer) {
      ctx.selection.clear();
      ctx.dispatch({ type: 'select', id: null });
    } else if (!ctx.selection.has(layer.id)) {
      ctx.selection.clear();
      ctx.selection.add(layer.id);
      ctx.dispatch({ type: 'select', id: layer.id });
    }
    if (!ctx.phone()) openContextMenu({ ctx, at: { clientX: event.clientX, clientY: event.clientY }, layer, anchor: stage });
  });
  stage.addEventListener('wheel', onWheel, { passive: false });
  stage.addEventListener('pointerdown', (event) => {
    if (event.target === stage || event.target === empty) ctx.dispatch({ type: 'select', id: null });
  });
  // The zone swallows scroll gestures (touch-action: none) only while a layer
  // is under the finger; the stage keeps the page scrollable everywhere else.
  document.addEventListener('keydown', onKeyDown, true);

  const stopReady = ctx.renderer.onReady?.(() => {
    for (const view of views.values()) {
      view.hash = undefined;
      view.scaleKey = undefined;
      view.measureKey = undefined;
      view.fitKey = undefined;
    }
    stage.classList.remove('is-draft');
    sync();
  });
  // Panels change the available canvas size without resizing the Designer root.
  const stopStageSize = observeSize(stage, relayout, 60);

  return {
    resetPan(){panX=0;panY=0;},
    startInlineCrop(id: string): boolean {
      inlineCrop?.close(false);
      inlineCrop = openInlineCrop({
        ctx,
        stage,
        geometry: () => ({ left: zoneLeft, top: zoneTop, scale }),
        hideLayer: (layerId, hide) => {
          const view = views.get(layerId);
          if (view) view.node.style.visibility = hide ? 'hidden' : '';
          stage.classList.toggle('is-inline-crop', hide);
        },
        openDialog: (layerId) => openCropDialog(ctx, layerId),
      }, id, () => { inlineCrop = undefined; });
      return Boolean(inlineCrop);
    },
    setProductBackdrop,
    productColor: () => (product && backdrop.kind === 'product' ? productRgb : undefined),
    element: main,
    relayout,
    sync,
    invalidate(layerId) {
      for (const [id, view] of views) {
        if (layerId && id !== layerId) continue;
        view.hash = undefined;
      }
      sync();
    },
    destroy() {
      inlineCrop?.close(false);
      stopped = true;
      stage.removeEventListener('wheel', onWheel);
      backingRequest++; window.clearTimeout(backingTimer);
      compositionRequest++;window.clearTimeout(compositionTimer);document.removeEventListener('keyup',releaseSpace,true);
      stopReady?.();
      stopStageSize();
      document.removeEventListener('keydown', onKeyDown, true);
      if (longPress !== undefined) window.clearTimeout(longPress);
      views.clear();
      main.remove();
    },
  };
}
